// --- Secure Storage Functions (formerly in secureStore.js) ---

const DB_NAME = "DataStore";
const DB_VERSION = 1;
const STORE_NAME = "property";

let dbInstance = null;

// 🗂 Open (or create) the IndexedDB database
function openIndexedDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "id" }); // Use "id" as keyPath
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onerror = (event) => {
      console.error("❌ IndexedDB error:", event.target.error);
      reject(event.target.error);
    };
  });
}

// 🔄 Get singleton DB instance (for performance)
async function getDB() {
  if (dbInstance) return dbInstance;
  dbInstance = await openIndexedDB();
  return dbInstance;
}

// 🔐 Store encrypted data with optional expiry
export async function storeDataToIndexDB(key, value, expiryDuration) {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    if (isNaN(expiryDuration)) {
      throw new Error("Invalid expiry duration");
    }
    const now = Date.now();
    const expiryTimestamp = now + expiryDuration;
    const expiryTimestampReadable = new Date(expiryTimestamp).toLocaleString();

    const tokenData = {
      id: key,
      value,
      expiryTimestamp,
      expiryTimestampReadable
    };

    await new Promise((resolve, reject) => {
      const request = store.put(tokenData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error("❌ Failed to store data in IndexedDB");
        reject(request.error);
      };
    });
    //await logAllDataFromIndexDB();
  } catch (error) {
    console.error("❌ IndexedDB store error:", error);
  }
}

// 🔓 Retrieve and validate stored data from IndexedDB
export async function getDataFromIndexDB(key) {
  try {
    await clearExpiredKeys();
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);

    //await logAllDataFromIndexDB();

    return new Promise((resolve, reject) => {
      const request = store.get(key);

      request.onsuccess = async () => {
        const result = request.result;

        if (!result) {
          console.warn(`🔍 No data found in storage for key: ${key}`);
          return resolve(null); // ✅ Return null instead of rejecting
        }

        const { value, expiryTimestamp } = result;
        //const expiryDate = new Date(expiryTimestamp).toLocaleString();
        //console.log(`⏱️ Token expiry timestamp for "${key}": ${expiryDate}`);

        if (Date.now() > expiryTimestamp) {
          console.warn("⚠️ Stored data expired. Clearing...");
          await clearDataFromIndexDB(key);
          return resolve(null); // ✅ Return null if expired
        }

        resolve(value); // ✅ Return string or stored value
      };

      request.onerror = () => {
        console.error(`❌ Failed to read key "${key}" from IndexedDB`);
        reject(new Error("❌ Failed to read data from storage"));
      };
    });

  } catch (error) {
    console.error("❌ IndexedDB read error:", error);
    throw error;
  }
}


// ✏️ Update value for a key without modifying expiryTimestamp
export async function updateDataInIndexDB(key, newValue) {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    // Get existing record first
    const existingRecord = await new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!existingRecord) {
      throw new Error(`No record found with key "${key}" to update`);
    }

    // Update only the value, keep expiryTimestamp intact
    const updatedRecord = {
      ...existingRecord,
      value: newValue,
    };

    await new Promise((resolve, reject) => {
      const request = store.put(updatedRecord);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error("❌ Failed to update data in IndexedDB");
        reject(request.error);
      };
    });
    await logAllDataFromIndexDB();
  } catch (error) {
    console.error("❌ IndexedDB update error:", error);
  }
}


// 🧹 Delete a specific key from the store
export async function clearDataFromIndexDB(key) {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const request = store.delete(key);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => {
        console.error("❌ Failed to delete key from IndexedDB");
        reject(request.error);
      };
    });
  } catch (error) {
    console.error("❌ Error clearing data:", error);
  }
}

// 🧹 Delete only expired entries from the store
export async function clearExpiredKeys() {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    // Open a cursor to iterate all records
    const request = store.openCursor();

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const { expiryTimestamp } = cursor.value;
        if (Date.now() > expiryTimestamp) {
          cursor.delete();
        }
        cursor.continue();
      }
    };

    request.onerror = () => {
      console.error("❌ Failed to clear expired keys");
    };
  } catch (error) {
    console.error("❌ Error clearing expired keys:", error);
  }
}

/*/ 🧹 Clear all data from the store (for logout or full reset)
export async function clearAllDataFromIndexDB() {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);

    await new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = () => {
        resolve();
      };
      clearRequest.onerror = () => {
        console.error("❌ Failed to clear all data from IndexedDB store");
        reject(clearRequest.error);
      };
    });
  } catch (error) {
    console.error("❌ Error clearing all data:", error);
  }
}*/

export async function clearAllDataFromIndexDB(logoutType) {
  try {
    const db = await getDB(); // Get the IndexedDB database instance
    const tx = db.transaction(STORE_NAME, "readwrite"); // Start a read-write transaction
    const store = tx.objectStore(STORE_NAME); // Get the object store

    if (logoutType === 'full-logout') {
      // If it's a full logout, clear all data from the store
      await new Promise((resolve, reject) => {
        const clearRequest = store.clear(); // Request to clear all objects in the store
        clearRequest.onsuccess = () => {
          //console.log(`✅ All data cleared from IndexedDB store: ${STORE_NAME} (Full Logout)`);
          resolve();
        };
        clearRequest.onerror = () => {
          console.error("❌ Failed to clear all data from IndexedDB store (Full Logout):", clearRequest.error);
          reject(clearRequest.error);
        };
      });
    } else {
      // If it's not a full logout, iterate and selectively delete, retaining 'email'
      await new Promise((resolve, reject) => {
        const request = store.openCursor(); // Open a cursor to iterate through records
        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            // If the current record's key is NOT 'email', delete it
            if (cursor.key !== 'email') {
              cursor.delete(); // Delete the current record
            }
            cursor.continue(); // Move to the next record
          } else {
            // Cursor has reached the end, all relevant records processed
            //console.log(`✅ Data cleared from IndexedDB store: ${STORE_NAME} (Partial Logout, 'email' retained)`);
            resolve();
          }
        };
        request.onerror = () => {
          console.error("❌ Failed to clear data from IndexedDB store (Partial Logout):", request.error);
          reject(request.error);
        };
      });
    }
  } catch (error) {
    console.error("❌ Error clearing data from IndexedDB:", error);
    // Re-throw the error for upstream handling if necessary
    throw error;
  }
}

// 🧾 Debug: Log all key-value-expiry entries in a user-friendly format
async function logAllDataFromIndexDB() {
  try {
    const db = await getDB();
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.openCursor();

    console.log("📦 [IndexedDB Debug Dump]");
    console.log("------------------------------------------------------------");

    request.onsuccess = (event) => {
      const cursor = event.target.result;
      if (cursor) {
        const { id, value, expiryTimestamp } = cursor.value;
        const now = Date.now();

        const expiryDate = new Date(expiryTimestamp).toLocaleString();
        const nowDate = new Date(now).toLocaleString();

        console.log(`🧩 Key: ${id}`);
        console.log(`   📌 Value:`, value);
        console.log(`   ⏱️  Expiry: ${expiryDate} (${expiryTimestamp})`);
        console.log(`   🕒  Now:    ${nowDate} (${now})`);
        console.log(`   ⏳ Status: ${now > expiryTimestamp ? "❌ Expired" : "✅ Valid"}`);
        console.log("------------------------------------------------------------");

        cursor.continue();
      } else {
        console.log("✅ Finished logging all IndexedDB entries.");
      }
    };

    request.onerror = () => {
      console.error("❌ Failed to iterate IndexedDB entries");
    };
  } catch (error) {
    console.error("❌ Error while logging IndexedDB entries:", error);
  }
}
